/** @jsxImportSource theme-ui */
import { Box, Flex, Grid } from "@bottlebooks/gatsby-theme-base/src";
import { FragmentType, graphql, useFragment } from "~/gql";
import BookAMeetingButton from "../Brand/BrandContacts/BookAMeetingButton";
import Contact from "../Brand/BrandContacts/Contact";
import ChatButton from "./ChatButton";
import useSiteConfig from "../useSiteConfig";

const fragment = graphql(`
  fragment RegistrationContacts on SingleRegistration {
    exhibitorContacts: contacts {
      ...Contact
    }
  }
`);

/**
 * A component to contain all the exhibitor details sections.
 * It can be used to prepend or append some content in shadowed components.
 */
export default function ExhibitorContacts({
  exhibitor: data,
  eventId,
  ...rest
}: {
  exhibitor: FragmentType<typeof fragment>;
  eventId: string;
}) {
  const { isChatEnabled, isMeetingBookingEnabled } = useSiteConfig();
  const exhibitor = useFragment(fragment, data);
  if (!hasContent(exhibitor)) return null;

  const distributionContacts = (exhibitor.brands || [])
    ?.flatMap((b) => b.distributionContacts || [])
    .filter(Boolean);
  const exhibitorContacts = exhibitor.exhibitorContacts || [];
  const allContacts = [...exhibitorContacts, ...distributionContacts];
  // Contacts may be entered multiple times.
  // We need to create a unique list and aggregate the contact types
  const uniqueContactsMap = allContacts.reduce((acc, contact) => {
    const existingContact = acc[contact.contactId];
    if (!existingContact) {
      acc[contact.contactId] = contact;
      return acc;
    }
    acc[contact.contactId] = {
      ...existingContact,
      contactTypes: [
        ...(existingContact?.contactTypes || []),
        ...(contact?.contactTypes || []),
      ].filter(Boolean),
    };
    return acc;
  }, {});
  const uniqueContacts = Object.values(uniqueContactsMap);
  return (
    <Grid
      sx={{ gridTemplateColumns: ["1fr", "1fr 1fr"], gap: ["1px", 3] }}
      width={300}
      {...rest}
    >
      {uniqueContacts?.map((contact) => (
        <Box
          sx={{ padding: 3.5, boxShadow: "smallest", borderRadius: "default" }}
          key={contact.contactId}
        >
          <Contact contact={contact}>
            <Flex gap={2} sx={{ marginTop: "auto", paddingTop: 2 }}>
              {isChatEnabled && <ChatButton />}
              {isMeetingBookingEnabled && (
                <BookAMeetingButton
                  collectionId={eventId}
                  contact={contact}
                  exhibitor={exhibitor}
                  visibility="ENABLED"
                  authentication="OPTIONAL"
                />
              )}
            </Flex>
          </Contact>
        </Box>
      ))}
    </Grid>
  );
}

function hasContent(exhibitor) {
  if (!exhibitor) return false;
  if (exhibitor.exhibitorContacts?.length) return true;
  if (exhibitor.brands?.[0]?.distributionContacts?.length) return true;
  return false;
}
